import type { GenUiConfig } from '../../../shared/ui/config.js';
import type { ChatUiHotelOffer, HotelPlannerBlock } from '@voll-intelligence/genui-contract';
import { panelScreenOf } from './hotel_panel_state.js';
import type { createHotelPlannerView } from './hotel_planner_view.js';
import {
  directivesToPanelQuery,
  useHotelPanelList,
  type HotelPanelList,
  type HotelPanelQuery,
} from './use_hotel_panel_list.js';
import { useNotifyOnContentChange, type PanelReport } from './use_panel_report.js';

/**
 * A CENA da lista (filtros + ordem do painel) nas mãos do host determinístico.
 * Existe para a bancada levar a cena a OUTRA aba e trazê-la de volta: a Página
 * do Hotel abre em aba nova no desktop, e voltar de lá à lista recomeçava do
 * zero (A3, QA 29/09). `initial` semeia o painel no lugar das diretivas do
 * bloco — e é também o estado de reset numa busca nova; `onChange` recebe cada
 * mudança de CONTEÚDO (não de identidade). É o vocabulário do PAINEL, exato: as
 * diretivas do fio encostam o piso de nota e capam comodidades, e a cena que
 * volta tem de ser a que foi.
 */
export type HotelPanelQueryPort = {
  readonly initial?: HotelPanelQuery;
  readonly onChange?: (query: HotelPanelQuery) => void;
};

/** O que a lista e a view leem da config do host — por prop: o organismo da
 *  conversa repassa a do Context, a página da bancada recebe a do host. */
export type HotelResultsConfig = Pick<
  GenUiConfig,
  'ports' | 'proxyImageUrl' | 'mapboxAccessToken' | 'hotelMapDefaultLocation'
>;

/** A gaveta de detalhes existe? Uma leitura, dois donos: o `view` do organismo e
 *  a lista completa (`usePlannerPanelList`) decidem `detailsAvailable` por ela. */
export function hasDetailsPort(config: HotelResultsConfig): boolean {
  return typeof config.ports?.hotelPlanner?.getHotelDetails === 'function';
}

/**
 * A lista completa do organismo: UMA para a lista cheia e para o mapa cheio.
 *
 * Fora do organismo porque são muitas entradas fixas e ele vive no teto de
 * linhas do lint. `ui` aqui é o bloco EFETIVO (ver `withDeterministicOffers`)
 * — é dele que sai a lista do bloco, que é o que o painel mostra até a primeira
 * página voltar.
 */
export function usePlannerPanelList(input: {
  readonly config: HotelResultsConfig;
  readonly locale: string;
  /** As ofertas cujo detalhe voltou sem quarto (#4469): saem com preço apagado. */
  readonly soldOut: ReadonlySet<string>;
  readonly ui: HotelPlannerBlock;
  readonly view: ReturnType<typeof createHotelPlannerView>;
  /** Alguma das duas superfícies está aberta? Fechadas não leem página. */
  readonly surfacesOpen: boolean;
  /** A cena da lista do host (só a página da bancada a passa). */
  readonly panelQuery?: HotelPanelQueryPort | undefined;
}): HotelPanelList {
  const { config, ui, panelQuery, locale, soldOut } = input;
  const list = useHotelPanelList({
    port: config.ports?.hotelPlanner,
    sessionId: ui.sessionId,
    enabled: input.surfacesOpen,
    blockViews: input.view.offers,
    blockOffers: ui.offers ?? [],
    stayPreferences: { onlyBreakfast: ui.onlyBreakfast, onlyRefundable: ui.onlyRefundable },
    nights: ui.nights,
    place: ui.placeDetails,
    locale,
    proxyImageUrl: config.proxyImageUrl,
    detailsAvailable: hasDetailsPort(config),
    // M1 · 1.2 — o painel nasce semeado pelas diretivas do agente; a cena do
    // host, quando vem, vence (é a que a pessoa já tinha montado).
    initialQuery: panelQuery?.initial ?? directivesToPanelQuery(ui.directives),
    keepQuery: ui.placeSource === 'map_area',
    soldOut,
  });
  // Avisa o host quando a cena MUDA, por conteúdo: o host só quer saber quando
  // filtro ou ordem trocaram.
  useNotifyOnContentChange(list.query, panelQuery?.onChange);
  return list;
}

/** M1 · Onda 2 (achado #4) — o relato do painel ao card não pode ecoar a
 *  PRÓPRIA diretiva do agente como se fosse fala do viajante: a semente é a
 *  das diretivas (a que abriu o painel em `usePlannerPanelList`) — `ui` aqui é
 *  o bloco EFETIVO, como lá. */
export function plannerScreenOf(
  list: HotelPanelList,
  surfacesOpen: boolean,
  effectiveOffers: readonly ChatUiHotelOffer[],
  ui: HotelPlannerBlock,
): PanelReport {
  return panelScreenOf(list, surfacesOpen, effectiveOffers, directivesToPanelQuery(ui.directives));
}
